/**
 * Sticky 粘性定位组件
 * 
 * 需要配合 StickyContainer 使用
 */
import React, { Component, cloneElement } from 'react';
import ReactDOM from 'react-dom';
import { StickyContext, StickyContainerContext } from '../StickyContainer';

export interface StickyChildProps {
  isSticky: boolean;
  wasSticky: boolean;
  distanceFromTop: number;
  distanceFromBottom: number;
  calculatedHeight: number;
  style: React.CSSProperties;
}

export interface StickyProps {
  /** 距顶部偏移量 */
  topOffset?: number;
  /** 距底部偏移量 */
  bottomOffset?: number;
  /** 是否相对定位 */
  relative?: boolean;
  /** 禁用高度补偿 */
  disableCompensation?: boolean;
  /** 禁用硬件加速 */
  disableHardwareAcceleration?: boolean;
  /** 渲染函数 */
  children: (props: StickyChildProps) => React.ReactElement;
}

interface StickyState {
  isSticky: boolean;
  wasSticky: boolean;
  distanceFromTop: number;
  distanceFromBottom: number;
  calculatedHeight: number;
  style: React.CSSProperties;
}

interface StickyContext {
  subscribe: (handler: (event: any) => void) => void;
  unsubscribe: (handler: (event: any) => void) => void;
  getParent: () => HTMLElement;
}

export default class Sticky extends Component<StickyProps, StickyState> {
  static defaultProps = {
    relative: false,
    topOffset: 0,
    bottomOffset: 0,
    disableCompensation: false,
    disableHardwareAcceleration: false,
  };

  static contextType = StickyContext;
  declare context: StickyContainerContext | null;

  state: StickyState = {
    isSticky: false,
    wasSticky: false,
    distanceFromTop: 0,
    distanceFromBottom: 0,
    calculatedHeight: 0,
    style: {},
  };

  private placeholder: HTMLDivElement | null = null;
  private content: Element | null = null;

  componentDidMount() {
    if (!this.context?.subscribe) {
      throw new TypeError('Expected Sticky to be mounted within StickyContainer');
    }
    this.context.subscribe(this.handleContainerEvent);
  }

  componentWillUnmount() {
    this.context?.unsubscribe(this.handleContainerEvent);
  }

  componentDidUpdate() {
    if (this.placeholder) {
      this.placeholder.style.paddingBottom = this.props.disableCompensation
        ? '0'
        : `${this.state.isSticky ? this.state.calculatedHeight : 0}px`;
    }
  }

  handleContainerEvent = ({ distanceFromTop, distanceFromBottom, eventSource }: any) => {
    const parent = this.context.getParent();
    const { topOffset = 0, bottomOffset = 0, relative, disableHardwareAcceleration } = this.props;

    let preventingStickyStateChanges = false;
    let adjustedDistanceFromTop = distanceFromTop;

    if (relative) {
      preventingStickyStateChanges = eventSource !== parent;
      adjustedDistanceFromTop = -(eventSource.scrollTop + eventSource.offsetTop) + (this.placeholder?.offsetTop || 0);
    }

    const placeholderClientRect = this.placeholder?.getBoundingClientRect();
    const contentClientRect = this.content?.getBoundingClientRect();
    const calculatedHeight = contentClientRect?.height || 0;

    const bottomDifference = distanceFromBottom - bottomOffset - calculatedHeight;

    const wasSticky = !!this.state.isSticky;
    const isSticky = preventingStickyStateChanges
      ? wasSticky
      : adjustedDistanceFromTop <= -topOffset && distanceFromBottom > -bottomOffset;

    const adjustedDistanceFromBottom = (relative ? parent.scrollHeight - parent.scrollTop : distanceFromBottom) - calculatedHeight;

    const style: React.CSSProperties = !isSticky
      ? {}
      : {
          position: 'fixed',
          top: bottomDifference > 0
            ? relative
              ? parent.offsetTop - (parent.offsetParent as HTMLElement)?.scrollTop
              : 0
            : bottomDifference,
          left: placeholderClientRect?.left,
          width: placeholderClientRect?.width,
        };

    if (!disableHardwareAcceleration) {
      style.transform = 'translateZ(0)';
    }

    this.setState({
      isSticky,
      wasSticky,
      distanceFromTop: adjustedDistanceFromTop,
      distanceFromBottom: adjustedDistanceFromBottom,
      calculatedHeight,
      style,
    });
  };

  render() {
    const element = cloneElement(
      this.props.children({
        isSticky: this.state.isSticky,
        wasSticky: this.state.wasSticky,
        distanceFromTop: this.state.distanceFromTop,
        distanceFromBottom: this.state.distanceFromBottom,
        calculatedHeight: this.state.calculatedHeight,
        style: this.state.style,
      }),
      {
        ref: (content: any) => {
          this.content = ReactDOM.findDOMNode(content) as Element;
        },
      }
    );

    return (
      <div>
        <div ref={(placeholder) => { this.placeholder = placeholder; }} />
        {element}
      </div>
    );
  }
}
